import { TitleCasePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Component, signal, inject, DestroyRef, viewChild, computed, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
// Utilities
import { getStatusDisplay, getStatusClass } from '@shared/utilities/status.util';
// Services
import { UserService } from '@core/services/user';
import { WorkshopService } from '@core/services/workshop';
// MDBootstrap
import { MdbTooltipModule } from 'mdb-angular-ui-kit/tooltip';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbLoadingModule } from 'mdb-angular-ui-kit/loading';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';
import { MdbValidationModule } from 'mdb-angular-ui-kit/validation';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { MdbCheckboxModule } from 'mdb-angular-ui-kit/checkbox';
import { MdbCollapseModule } from 'mdb-angular-ui-kit/collapse';
import { MdbDatepickerComponent, MdbDatepickerModule } from 'mdb-angular-ui-kit/datepicker';
import { MdbModalModule, MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { MdbTableModule, MdbTableDirective } from 'mdb-angular-ui-kit/table';
import { MdbNotificationService } from 'mdb-angular-ui-kit/notification';
//shared components
import { Toast } from '@shared/components/toast/toast';
import { Gradientsegmentbar } from '@shared/components/gradientsegmentbar/gradientsegmentbar';
import { WorkshopModal } from '@shared/components/workshopmodal/workshopmodal';
import { Workshop } from '@core/models/workshop';
import { getWorkshopAriaLabel, getWorkshopSeatBadgeClass, getWorkshopButtonClass, getWorkshopButtonIcon, getWorkshopModalMode, getWorkshopStatusClass, getFriendlyStatus, getWorkshopButtonText, formatWorkshopDateTime, formatWorkshopMode, formatWorkshopTime, formatWorkshopDateOnly } from '@shared/utilities/workshop.util';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';

@Component({
  selector: 'app-search-workshops',
  imports: [
    TitleCasePipe, MdbTooltipModule, ReactiveFormsModule, MdbFormsModule,
    MdbValidationModule, Gradientsegmentbar, MdbLoadingModule, MdbCheckboxModule,
    MdbSelectModule, MdbRippleModule, CommonModule, MdbCollapseModule,
    MdbDatepickerModule, MdbModalModule, RouterModule, MdbTableModule,
  ],
  providers: [MdbNotificationService],
  templateUrl: './search-workshops.html',
  styleUrl: './search-workshops.scss',
})
export class SearchWorkshops {
  protected startDatepicker = viewChild<MdbDatepickerComponent>('startDatepicker');
  private destroyRef = inject(DestroyRef);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private notificationService = inject(MdbNotificationService);
  private workshopService = inject(WorkshopService);
  private modalService = inject(MdbModalService);

  private table = viewChild.required<MdbTableDirective<Workshop>>('table');
  private searchInput = viewChild<ElementRef>('searchInput');
  private resultsCard = viewChild<ElementRef>('resultsCard');

  modalRef: MdbModalRef<WorkshopModal> | null = null;
  dataSource = signal<Workshop[]>([]);
  protected endDate = new FormControl<Date | null>(null);
  protected startDate = new FormControl<Date | null>(null);

  isLoading = signal<boolean>(false);
  //client side filtering 
  onlyShowAvailable = signal<boolean>(true);
  //mobile rendering
  isMobile = signal<boolean>(window.innerWidth < 768);

  // functions have to be set to class properties in order for the template to have access to them. 
  protected getWorkshopStatusClass = getWorkshopStatusClass;
  protected getWorkshopSeatBadgeClass = getWorkshopSeatBadgeClass;
  protected getWorkshopButtonClass = getWorkshopButtonClass;
  protected getWorkshopButtonIcon = getWorkshopButtonIcon;
  protected getWorkshopButtonText = getWorkshopButtonText;
  protected getWorkshopAriaLabel = getWorkshopAriaLabel;
  protected getWorkshopFriendlyStatus = getFriendlyStatus;
  protected formatWorkshopDateTime = formatWorkshopDateTime;
  protected formatWorkshopDateOnly = formatWorkshopDateOnly;
  protected formatWorkshopTime = formatWorkshopTime;
  protected formatWorkshopMode = formatWorkshopMode;


  headers: { label: string; field: keyof Workshop }[] = [
    { label: 'Date', field: 'sessionStartTimestamp' },
    { label: 'Topic', field: 'topic' },
    { label: 'Workshop', field: 'title' },
    { label: 'Room', field: 'room' },
    { label: 'Format', field: 'mode' },
    { label: 'Language', field: 'language' },
    { label: 'Status', field: 'sessionStatus' },
    { label: 'Register/Cancel', field: 'workshopSessionId' }, // unsortable column, just needs a valid field
  ];
  constructor() {
    const { start, end } = this.getDefaultDateRange();
    this.startDate.setValue(start);
    this.endDate.setValue(end);

  }

  onSearch(): void {
    const start = this.startDate.value ? new Date(this.startDate.value) : null;
    const end = this.endDate.value ? new Date(this.endDate.value) : null;

    if (start && end && start > end) {
      //TODO: Validation error message / styling
      // show an error — start can't be after end
      return;
    }

    if (!start || !end) return;

    const startOfDay = new Date(start);
    startOfDay.setUTCHours(0, 0, 0, 0);
    
    const endOfDay = new Date(end);
    endOfDay.setUTCHours(23, 59, 59, 999);

    this.isLoading.set(true);
    this.workshopService.getWorkshops(startOfDay.toISOString(), endOfDay.toISOString())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.dataSource.set(data);
        this.isLoading.set(false);
        console.log(data);
        setTimeout(() => this.scrollToResults(), 300);
      });
  }

  private scrollToResults(): void {
    if (this.isMobile()) {
      this.resultsCard()?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  search(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.table().search(term);
  }

  openModal(workshop: Workshop): void {
    const mode = getWorkshopModalMode(workshop);
    this.modalRef = this.modalService.open(WorkshopModal, {
      modalClass: 'modal-dialog-centered modal-xl modal-fullscreen-md-down',
      data: { workshop, mode }
    });
    //handle response
    this.modalRef.onClose.subscribe(() => {
      const result = this.modalRef?.component?.result;
      if (!result) return;
      this.notificationService.open(Toast, {
        data: {
          mode: result.success ? 'success' : 'error',
          message: result.message
        },
        position: 'top-center',
        autohide: true,
        delay: 5000
      });
      //refresh results with the latest so we see the newly registered workshop
      if (result.success) {
        //TODO: patch the single record (?)
        this.onSearch();
      }
    });

  }

  //#region client side filtering
  filteredSource = computed(() => {
    return this.dataSource()
    .filter(w => !this.onlyShowAvailable() || (w.sessionStatus === 'Scheduled' && (w.seatsRemaining ?? 0) > 0));
    });
  //TODO: Determine how we display waitlisted items

  onResetFilters(): void {
    this.onlyShowAvailable.set(false);
    this.table().search('');
    this.searchInput()!.nativeElement.value = '';
  }
  //#endregion


  //#region Helpers
  private formatDateForPicker(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    //example: "04/03/2026"
  }

  private getDefaultDateRange(): { start: Date; end: Date } {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 30);
    return { start, end };
  }
  //#endregion
}